import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, X } from 'lucide-react';

const STORAGE_KEY = 'home_students_2027_popup_dismissed';

/** Min time on page before the sheet can open (avoids intrusive interstitial on land). */
const MIN_DWELL_MS = 8000;
/** Extra pause after scroll/tap before showing. */
const AFTER_ENGAGE_MS = 1200;
const SCROLL_THRESHOLD_PX = 140;

const CRAWLER_UA =
  /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|twitterbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest|applebot|semrushbot|ahrefsbot|mj12bot|dotbot|petalbot|bytespider|reactsnap/i;

interface Students2027RegisterPopupProps {
  enabled: boolean;
}

function shouldSkipForBot(): boolean {
  if (typeof navigator === 'undefined') return true;
  if (navigator.userAgent === 'ReactSnap') return true;
  return CRAWLER_UA.test(navigator.userAgent);
}

const Students2027RegisterPopup: React.FC<Students2027RegisterPopupProps> = ({ enabled }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled || shouldSkipForBot()) {
      setIsOpen(false);
      setVisible(false);
      return;
    }

    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      // ignore storage errors
    }

    let cancelled = false;
    let openTimer: number | undefined;
    let engaged = false;
    const pageReadyAt = Date.now();

    const tryOpen = () => {
      if (cancelled || engaged) return;
      engaged = true;

      const wait = Math.max(0, MIN_DWELL_MS - (Date.now() - pageReadyAt)) + AFTER_ENGAGE_MS;
      openTimer = window.setTimeout(() => {
        if (cancelled) return;
        setIsOpen(true);
        window.requestAnimationFrame(() => setVisible(true));
      }, wait);
    };

    const onScroll = () => {
      if (window.scrollY >= SCROLL_THRESHOLD_PX) {
        tryOpen();
        cleanupEngage();
      }
    };

    const onEngage = () => {
      tryOpen();
      cleanupEngage();
    };

    const cleanupEngage = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointerdown', onEngage);
      window.removeEventListener('keydown', onEngage);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointerdown', onEngage, { passive: true });
    window.addEventListener('keydown', onEngage);

    return () => {
      cancelled = true;
      if (openTimer) window.clearTimeout(openTimer);
      cleanupEngage();
    };
  }, [enabled]);

  const dismiss = () => {
    setVisible(false);
    window.setTimeout(() => setIsOpen(false), 200);
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore storage errors
    }
  };

  const handleRegister = () => {
    dismiss();
    navigate('/register');
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-end justify-center p-3 sm:items-center sm:p-4 transition-opacity duration-200 ${
        visible ? 'bg-black/45 opacity-100 backdrop-blur-[2px]' : 'bg-black/0 opacity-0'
      }`}
      dir="rtl"
      onClick={dismiss}
      role="presentation"
    >
      <div
        className={`relative w-full max-w-sm overflow-hidden rounded-2xl border border-[#E5DCCF] bg-[#FAF7F0] shadow-2xl shadow-[#001848]/12 transition-all duration-300 ${
          visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-4 scale-95 opacity-0 sm:translate-y-2'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="students-2027-popup-title"
      >
        <div className="h-1.5 w-full bg-gradient-to-l from-[#CC9C54] via-[#C09048] to-[#A87824]" />

        <button
          type="button"
          onClick={dismiss}
          className="absolute left-2 top-3.5 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-[#5A6B82] hover:bg-[#EFE8DC] hover:text-[#001848]"
          aria-label="إغلاق"
        >
          <X size={18} />
        </button>

        <div className="px-5 pb-5 pt-6 text-center sm:px-6 sm:pb-6 sm:pt-7">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EFE8DC] text-[#001848] ring-1 ring-[#E5DCCF] sm:h-16 sm:w-16">
            <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.75} />
          </div>

          <p className="mb-2 text-xs font-semibold tracking-wide text-[#A87824] sm:text-sm">
            دفعة 2027
          </p>

          <h2
            id="students-2027-popup-title"
            className="font-cairo-bold text-2xl leading-snug text-[#001848] sm:text-[1.7rem]"
          >
            يا طلاب 2027!
          </h2>

          <p className="mx-auto mt-2.5 max-w-[17.5rem] text-sm leading-relaxed text-[#5A6B82] sm:text-base">
            الفرصة قدامك دلوقتي — ادخل وسجّل حسابك وابدأ معانا من بدري.
          </p>

          <div className="mt-6">
            <button
              type="button"
              onClick={handleRegister}
              className="flex w-full min-h-12 items-center justify-center rounded-full bg-[#C09048] px-4 text-sm font-bold text-white shadow-md shadow-[#C09048]/30 transition hover:brightness-105 sm:text-base"
            >
              سجل الآن
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Students2027RegisterPopup;
